import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { searchResources } from '../services/api';

function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cache, setCache] = useState({});

  // 从URL获取搜索参数
  const getSearchParamsFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    return {
      keyword: searchParams.get('keyword') || '',
      page: parseInt(searchParams.get('page') || '1', 10),
      limit: parseInt(searchParams.get('limit') || '8', 10),
      category: searchParams.get('category') || ''
    };
  };

  const fetchSearchResults = useCallback(async (params) => {
    const { keyword, page, limit, category } = params;
    
    if (!keyword) {
      navigate('/');
      return;
    }

    const cacheKey = `${keyword}-${page}-${limit}-${category}`;
    if (cache[cacheKey]) {
      setSearchData(cache[cacheKey]);
      return;
    }

    setLoading(true);
    try {
      const response = await searchResources(keyword, page, limit, category);
      console.log('搜索结果:', response);
      
      if (response && response.code === 0 && Array.isArray(response.data)) {
        const newData = {
          data: response.data,
          total: response.total || 0,
          ...params
        };
        
        setCache(prev => ({
          ...prev,
          [cacheKey]: newData
        }));
        setSearchData(newData);
      } else {
        console.error('搜索结果格式不正确:', response);
        const emptyData = {
          data: [],
          total: 0,
          ...params
        };
        setSearchData(emptyData);
        setCache(prev => ({
          ...prev,
          [cacheKey]: emptyData
        }));
      }
    } catch (error) {
      console.error('搜索失败:', error);
      setSearchData({
        data: [],
        total: 0,
        ...params
      });
    } finally {
      setLoading(false);
    }
  }, [cache, navigate]);

  useEffect(() => {
    const urlParams = getSearchParamsFromURL();
    fetchSearchResults(urlParams);
  }, [location.search, fetchSearchResults]);

  const handlePageChange = (newPage) => {
    const urlParams = getSearchParamsFromURL();
    const newParams = new URLSearchParams({
      ...urlParams,
      page: newPage
    });
    
    navigate(`/search?${newParams.toString()}`);
    window.scrollTo(0, 0);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('链接已复制到剪贴板');
    } catch (err) {
      console.error('复制失败:', err);
      alert('复制失败，请手动复制');
    }
  };

  if (loading) {
    return <div className="loading">加载中...</div>;
  }

  if (!searchData?.data?.length) {
    return (
      <div className="no-results">
        <h2>未找到相关资源</h2>
        <p>换个关键词试试看？</p>
        <button onClick={() => navigate('/')}>返回首页</button>
      </div>
    );
  }

  const totalPages = Math.ceil(searchData.total / searchData.limit);

  return (
    <div className="search-results">
      <div className="search-header">
        <h2>"{searchData.keyword}"的搜索结果 (共 {searchData.total} 个)</h2>
        <button onClick={() => navigate('/')}>返回首页</button>
      </div>
      
      <div className="results-list">
        {searchData.data.map((item, index) => (
          <div key={index} className="result-item">
            <div className="result-content">
              <div className="result-title-row">
                <h3 className="result-title">{item.title}</h3>
              </div>
              {item.description && (
                <p className="result-desc">{item.description}</p>
              )}
              <div className="result-meta">
                <span className="result-category">{item.category}</span>
                <span className="result-date">时间: {item.updated_at}</span>
              </div>
            </div>
            <div className="result-actions">
              <a href={item.link} target="_blank" rel="noopener noreferrer">查看详情</a>
              <button onClick={() => handleCopy(item.link)}>复制链接</button>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(searchData.page - 1)}
            disabled={searchData.page === 1}
            className="page-button"
          >
            上一页
          </button>
          <span className="page-info">
            {searchData.page} / {totalPages}
          </span>
          <button 
            onClick={() => handlePageChange(searchData.page + 1)}
            disabled={searchData.page === totalPages}
            className="page-button"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}
export default SearchResults;
